import { pickT } from "@repo/shared";
import { useMemo } from "react";
import { PieChart } from "@/components/charts/pie-chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getRelativeTime } from "@/lib/utils";
import { TABLEAU20_COLORS } from "@/lib/utils/vega-chart-shared";
import { T } from "./xray-pie-chart.i18n";

type XRayPieChartProps = {
  data: Array<{
    id: string;
    label: string;
    value: number;
    /** Explicit slice color — used by comparison views to keep a category's color identical across charts. */
    color?: string;
  }>;
  height?: number;
  timestamp?: string;
  showChart?: boolean;
  showTable?: boolean;
  chartColors?: string[] | null;
  categoryLabel?: string;
  language?: string;
  coverage?: {
    totalProducts: number;
    coveredProducts: number;
    originalWeightPct: number;
  };
};

export function XRayPieChart({
  data,
  height = 280,
  timestamp,
  showChart = true,
  showTable = false,
  chartColors,
  categoryLabel = "Asset Class",
  language = "en_GB",
  coverage,
}: XRayPieChartProps) {
  const colors = chartColors || TABLEAU20_COLORS;
  const t = pickT(T, language);

  // Memoize sorted data and color styles to prevent recreating on every render
  const sortedData = useMemo(() => [...data].sort((a, b) => b.value - a.value), [data]);

  const colorStyles = useMemo(
    () =>
      sortedData.map((item, index) => ({
        backgroundColor: item.color ?? colors[index % colors.length],
      })),
    [sortedData, colors],
  );

  // Format coverage text with placeholders
  // QL-304 phase 2 — weight-based: the note fires when the calculation covers
  // less than the whole portfolio by WEIGHT, not by product count (an
  // unmatchable holding pre-drops from the counts but still shifts the value).
  const coveredPct = coverage ? Math.round(coverage.originalWeightPct) : 100;
  const getCoverageText = () => (!coverage ? "" : t.missingDataNote(String(coveredPct)));

  return (
    <div className="w-full space-y-2">
      {showChart && (
        <div style={{ height }}>
          <PieChart
            data={sortedData}
            width={350}
            height={height}
            innerRadius={0.5}
            chartColors={colors}
            lang={language}
          />
        </div>
      )}
      {showTable && (
        <Table variant="data">
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>{categoryLabel}</TableHead>
              <TableHead className="text-right">{t.allocation}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>
                  <div className="w-4 h-4 rounded-sm" style={colorStyles[index]} />
                </TableCell>
                <TableCell>{item.label}</TableCell>
                <TableCell className="text-right">{item.value.toFixed(1)}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {(coverage || timestamp) && (
        <div className="text-xs text-muted-foreground">
          {coverage && coverage.originalWeightPct < 99.995 && <div>{getCoverageText()}</div>}
          {timestamp && <div>{t.snapshotTaken(getRelativeTime(timestamp, language))}</div>}
        </div>
      )}
    </div>
  );
}
